import {
  CSATData,
  CustomerSatisfactionSheet,
  FeedbackMediaType,
} from "@amzn/stencil-react-components/customer-satisfaction";
import { Container } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContainer } from "@amzn/stencil-react-components/modal";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useCallback, useMemo, useState } from "react";

const IMAGE_PATH = "/img/cfm-character.svg";

export interface CustomerFeedbackSheetProps {
  touchpoint: string;
  close: () => void;
}

export const CustomerFeedbackSheet: FC<CustomerFeedbackSheetProps> = ({ touchpoint, close }) => {
  const [hasErrors, setHasErrors] = useState(false);
  const { addMessageBanner } = useMessageBannersBox();
  const storageKey = useMemo(() => `feedback-score-${touchpoint}`, [touchpoint]);
  const [score, setScore] = useState<number | undefined>(() => {
    const saved = sessionStorage.getItem(storageKey);
    const parsed = saved ? Number(saved) : undefined;
    return Number.isFinite(parsed) ? parsed : undefined;
  });

  const mediaProps = useMemo(
    () => ({
      media: {
        altText: getString("customerFeedback.sheet.imageAltText"),
        src: IMAGE_PATH,
        type: FeedbackMediaType.IMAGE,
      },
    }),
    []
  );

  const handleScoreChange = useCallback(
    (newScore: number) => {
      setHasErrors(false);
      setScore(newScore);
      sessionStorage.setItem(storageKey, String(newScore));
    },
    [storageKey]
  );

  const handleCancel = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    close();
  }, [storageKey, close]);

  const handleSubmit = useCallback(
    (data: CSATData) => {
      setHasErrors(false);
      const finalScore = Number.isFinite(data.selectedRating) ? data.selectedRating! + 1 : score;
      if (finalScore === undefined) {
        setHasErrors(true);
        return;
      }
      trackUxmCustomEvent(`cfm-sheet-${touchpoint}`, { score: finalScore, comment: data.optionalData });
      addMessageBanner(getString("customerFeedback.sheet.successMessage"), MessageBannerType.Success);

      sessionStorage.removeItem(storageKey);
      close();
    },
    [touchpoint]
  );

  return (
    <ModalContainer maxWidth="S1200">
      <Container paddingHorizontal="S500">
        <CustomerSatisfactionSheet
          desktopMediaProps={mediaProps}
          mobileMediaProps={mediaProps}
          ratingProps={{
            onChange: handleScoreChange,
            options: getString("customerFeedback.sheet.ratingOptions") as unknown as string[],
            titleText: getString("customerFeedback.sheet.ratingTitle"),
            value: score,
          }}
          feedbackCardProps={{
            hasErrors,
            hasOptionalTextArea: true,
            optionalContentLabel: getString("customerFeedback.sheet.feedbackLabel"),
          }}
          onCancelClick={handleCancel}
          onLaterClick={close}
          titleText={getString("customerFeedback.sheet.title")}
          onSubmit={handleSubmit}
        />
      </Container>
    </ModalContainer>
  );
};
